import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Key, Users, Shield, ClipboardList, Plus, Edit2, Trash2, Clock, User, Activity, RefreshCw, Loader2, Search, Check, Building2, UserPlus } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { authService } from '../../services/authService';

// Types aligned with RBAC auth API
interface RBACUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: number | boolean;
  is_global_admin: number | boolean;
  last_login?: string;
  created_at?: string;
  roles?: string;
  role_slugs?: string;
  phone?: string;
}

interface RBACRole {
  id: number;
  name: string;
  slug: string;
  role_type: string;
  description?: string;
  user_count?: number;
}

interface AuditLogEntry {
  id: number;
  created_at: string;
  email?: string;
  target_email?: string;
  action: string;
  description?: string;
  details?: string;
  ip_address?: string;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isGlobalAdmin?: boolean;
}

type SettingsTab = 'general' | 'users' | 'roles' | 'audit' | 'organizations';

interface OrgOption {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  contact_email?: string;
  license_type?: string;
  max_users?: number;
  user_count?: number;
  openai_api_key?: string | null;
  openai_model?: string | null;
  created_at?: string;
}

interface NewOrgForm {
  name: string;
  description: string;
  contact_email: string;
  license_type: string;
  max_users: number;
}

interface NewOrgAdminForm {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, isGlobalAdmin = false }) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<SettingsTab>(isGlobalAdmin ? 'general' : 'users');
  const [apiKey, setApiKey] = useState('');
  const [apiModel, setApiModel] = useState('gpt-4o');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [savingApiKey, setSavingApiKey] = useState(false);
  
  // Organizations for global admin
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number>(0);
  
  // Users state
  const [users, setUsers] = useState<RBACUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [roles, setRoles] = useState<RBACRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  
  // Audit state
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTotal, setAuditTotal] = useState(0);
  
  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<RBACUser | null>(null);
  const [userForm, setUserForm] = useState<{
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    role_id: number;
  }>({ email: '', first_name: '', last_name: '', password: '', role_id: 0 });
  const [savingUser, setSavingUser] = useState(false);
  
  // Role form state
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [deletingRoleId, setDeletingRoleId] = useState<number | null>(null);
  
  // RBAC Permissions state
  const [editingRolePerms, setEditingRolePerms] = useState<RBACRole | null>(null);
  const [modules, setModules] = useState<Array<{ id: number; name: string; slug: string; sections: Array<{ id: number; name: string; slug: string }> }>>([]);
  const [permissionTypes, setPermissionTypes] = useState<Array<{ id: number; name: string; slug: string }>>([]);
  const [permMatrix, setPermMatrix] = useState<Record<string, Record<number, boolean>>>({});
  const [savingPerms, setSavingPerms] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(false);
  
  // GRC sections (static list to avoid duplicates)
  const GRC_SECTIONS = [
    { key: 'overview', name: 'Overview' },
    { key: 'controls', name: 'Controls' },
    { key: 'risks', name: 'Risks' },
    { key: 'assets', name: 'Assets' },
    { key: 'policies', name: 'Policies' },
    { key: 'audits', name: 'Audits' },
    { key: 'incidents', name: 'Incidents' },
    { key: 'suppliers', name: 'Suppliers' },
    { key: 'analytics', name: 'Analytics' },
    { key: 'programs', name: 'Programs' },
  ];
  
  // Search filter for audit log
  const [auditSearch, setAuditSearch] = useState('');
  
  // Organization management state (global admin only)
  const [allOrganizations, setAllOrganizations] = useState<OrgOption[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [newOrgForm, setNewOrgForm] = useState<NewOrgForm>({
    name: '', description: '', contact_email: '', license_type: 'standard', max_users: 10
  });
  const [savingOrg, setSavingOrg] = useState(false);
  const [deletingOrgId, setDeletingOrgId] = useState<number | null>(null);
  const [showOrgAdminForm, setShowOrgAdminForm] = useState<number | null>(null);
  const [newOrgAdminForm, setNewOrgAdminForm] = useState<NewOrgAdminForm>({
    email: '', first_name: '', last_name: '', password: ''
  });
  const [savingOrgAdmin, setSavingOrgAdmin] = useState(false);

  // Load organizations for API key management (global admin only)
  const loadOrganizations = useCallback(async () => {
    if (!isGlobalAdmin) return;
    try {
      const orgs = await authService.getOrganizations();
      setOrganizations(orgs as OrgOption[]);
      if (orgs.length > 0 && selectedOrgId === 0) {
        setSelectedOrgId(orgs[0].id);
        // Load current API key for first org
        const firstOrg = orgs[0] as OrgOption;
        if (firstOrg.openai_api_key) {
          setApiKey(firstOrg.openai_api_key);
        }
        if (firstOrg.openai_model) {
          setApiModel(firstOrg.openai_model);
        }
      }
    } catch (e) {
      console.error('Failed to load organizations:', e);
    }
  }, [isGlobalAdmin, selectedOrgId]);

  useEffect(() => {
    if (isOpen && isGlobalAdmin && activeTab === 'general') {
      loadOrganizations();
    }
    setError('');
  }, [isOpen, isGlobalAdmin, activeTab, loadOrganizations]);

  // When org selection changes, load its API key
  useEffect(() => {
    if (selectedOrgId > 0 && organizations.length > 0) {
      const org = organizations.find(o => o.id === selectedOrgId);
      if (org) {
        setApiKey(org.openai_api_key || '');
        setApiModel(org.openai_model || 'gpt-4o');
      }
    }
  }, [selectedOrgId, organizations]);

  // Load all organizations for organization management tab
  const loadAllOrganizations = useCallback(async () => {
    if (!isGlobalAdmin) return;
    setOrgsLoading(true);
    try {
      const orgs = await authService.getOrganizations();
      setAllOrganizations(orgs as OrgOption[]);
    } catch (e) {
      console.error('Failed to load all organizations:', e);
    } finally {
      setOrgsLoading(false);
    }
  }, [isGlobalAdmin]);

  // Load organizations when organizations tab is active
  useEffect(() => {
    if (isOpen && isGlobalAdmin && activeTab === 'organizations') {
      loadAllOrganizations();
    }
  }, [isOpen, isGlobalAdmin, activeTab, loadAllOrganizations]);

  // Create new organization
  const handleCreateOrganization = async () => {
    if (!newOrgForm.name.trim()) {
      setError('Organization name is required');
      return;
    }
    setSavingOrg(true);
    setError('');
    try {
      const result = await authService.createOrganization({
        name: newOrgForm.name.trim(),
        description: newOrgForm.description.trim() || undefined,
        contact_email: newOrgForm.contact_email.trim() || undefined,
        license_type: newOrgForm.license_type || 'standard',
        max_users: newOrgForm.max_users || 10,
      });
      if (result.success) {
        setShowOrgForm(false);
        setNewOrgForm({ name: '', description: '', contact_email: '', license_type: 'standard', max_users: 10 });
        await loadAllOrganizations();
      } else {
        setError(result.error || 'Failed to create organization');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to create organization');
    } finally {
      setSavingOrg(false);
    }
  };

  // Delete organization
  const handleDeleteOrganization = async (orgId: number) => {
    if (!confirm('Are you sure you want to delete this organization? This will delete ALL users, roles, and data for this organization. This cannot be undone!')) {
      return;
    }
    setDeletingOrgId(orgId);
    setError('');
    try {
      const result = await authService.deleteOrganization(orgId);
      if (result.success) {
        await loadAllOrganizations();
      } else {
        setError(result.error || 'Failed to delete organization');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to delete organization');
    } finally {
      setDeletingOrgId(null);
    }
  };

  // Create org admin
  const handleCreateOrgAdmin = async (orgId: number) => {
    if (!newOrgAdminForm.email || !newOrgAdminForm.first_name || !newOrgAdminForm.password) {
      setError('Email, first name, and password are required');
      return;
    }
    if (newOrgAdminForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSavingOrgAdmin(true);
    setError('');
    try {
      const result = await authService.createOrgAdmin(orgId, {
        email: newOrgAdminForm.email.trim(),
        first_name: newOrgAdminForm.first_name.trim(),
        last_name: newOrgAdminForm.last_name.trim() || undefined,
        password: newOrgAdminForm.password,
      });
      if (result.success) {
        setShowOrgAdminForm(null);
        setNewOrgAdminForm({ email: '', first_name: '', last_name: '', password: '' });
        await loadAllOrganizations();
      } else {
        setError(result.error || 'Failed to create organization admin');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to create organization admin');
    } finally {
      setSavingOrgAdmin(false);
    }
  };

  // Load users from RBAC auth API
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await authService.getUsers();
      setUsers(data);
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Load roles from RBAC auth API
  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const data = await authService.getRolesList();
      setRoles(data);
    } catch (e) {
      console.error('Failed to load roles:', e);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  // Load audit log from RBAC auth API
  const loadAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const result = await authService.getAuditLog(1, 100);
      setAuditLog(result.data || []);
      setAuditTotal(result.total || 0);
    } catch (e) {
      console.error('Failed to load audit log:', e);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && (activeTab === 'users' || activeTab === 'roles')) {
      loadUsers();
      loadRoles();
    }
    if (isOpen && activeTab === 'audit') loadAuditLog();
  }, [isOpen, activeTab, loadUsers, loadRoles, loadAuditLog]);

  const handleSaveSettings = async () => {
    if (selectedOrgId <= 0) {
      setError('Please select an organization');
      return;
    }
    
    setSavingApiKey(true);
    setError('');
    try {
      const result = await authService.setOrgApiKey(selectedOrgId, apiKey.trim(), apiModel);
      if (result.success) {
        // Update local state to reflect saved value
        setOrganizations(prev => prev.map(org => 
          org.id === selectedOrgId 
            ? { ...org, openai_api_key: apiKey.trim() || null, openai_model: apiModel }
            : org
        ));
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } else {
        setError(result.error || 'Failed to save API key');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to save API key');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({ email: '', first_name: '', last_name: '', password: '', role_id: roles.length > 0 ? roles[0].id : 0 });
    setShowUserForm(true);
    setError('');
  };

  const handleEditUser = (user: RBACUser) => {
    setEditingUser(user);
    const userRoleSlugs = user.role_slugs?.split(', ') || [];
    const matchedRole = roles.find(r => userRoleSlugs.includes(r.slug));
    setUserForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name || '',
      password: '',
      role_id: matchedRole?.id || 0,
    });
    setShowUserForm(true);
    setError('');
  };

  const handleSaveUser = async () => {
    if (!userForm.email || !userForm.first_name) {
      setError('Email and first name are required');
      return;
    }
    if (!editingUser && userForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setSavingUser(true);
    setError('');
    try {
      if (editingUser) {
        const updateData: Record<string, any> = {
          email: userForm.email,
          first_name: userForm.first_name,
          last_name: userForm.last_name,
        };
        await authService.updateUser(editingUser.id, updateData);
        
        if (userForm.password) {
          await authService.resetPassword(editingUser.id, userForm.password);
        }
        
        if (userForm.role_id > 0) {
          await authService.assignRole(editingUser.id, userForm.role_id);
        }
      } else {
        const result = await authService.createUser({
          email: userForm.email,
          first_name: userForm.first_name,
          last_name: userForm.last_name || undefined,
          password: userForm.password,
          role_id: userForm.role_id > 0 ? userForm.role_id : undefined,
        });
        
        if (!result.success) {
          setError(result.error || 'Failed to create user');
          setSavingUser(false);
          return;
        }
      }
      
      setShowUserForm(false);
      setEditingUser(null);
      setUserForm({ email: '', first_name: '', last_name: '', password: '', role_id: 0 });
      await loadUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to save user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleToggleUser = async (user: RBACUser) => {
    const newActive = !user.is_active;
    try {
      await authService.toggleUser(user.id, newActive);
      await loadUsers();
    } catch (e) {
      console.error('Toggle user failed:', e);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    setSavingRole(true);
    try {
      const result = await authService.createRole(newRoleName.trim(), newRoleDesc.trim());
      if (result.success) {
        setShowRoleForm(false);
        setNewRoleName('');
        setNewRoleDesc('');
        await loadRoles();
      }
    } catch (e) {
      console.error('Create role failed:', e);
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId: number, roleName: string) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone.`)) {
      return;
    }
    setDeletingRoleId(roleId);
    try {
      const result = await authService.deleteRole(roleId);
      if (result.success) {
        await loadRoles();
      } else {
        setError(result.error || 'Failed to delete role');
      }
    } catch (e: any) {
      console.error('Delete role failed:', e);
      setError(e.message || 'Failed to delete role');
    } finally {
      setDeletingRoleId(null);
    }
  };

  // Load modules for permissions matrix
  const loadModules = useCallback(async () => {
    try {
      const data = await authService.getModules();
      setModules(data || []);
    } catch (e) {
      console.error('Failed to load modules:', e);
    }
  }, []);

  // Load permissions for a specific role
  const handleEditRolePermissions = async (role: RBACRole) => {
    setEditingRolePerms(role);
    setLoadingPerms(true);
    try {
      await loadModules();
      const result = await authService.getPermissionsMatrix(role.id);
      if (result.success) {
        setPermissionTypes(result.permission_types || []);
        setPermMatrix(result.permissions || {});
      }
    } catch (e) {
      console.error('Failed to load permissions:', e);
    } finally {
      setLoadingPerms(false);
    }
  };

  // Toggle permission in matrix
  const togglePermission = (moduleId: number, sectionId: number, permTypeId: number) => {
    const key = `${moduleId}-${sectionId}`;
    setPermMatrix(prev => {
      const current = prev[key] || {};
      return {
        ...prev,
        [key]: { ...current, [permTypeId]: !current[permTypeId] }
      };
    });
  };

  // Get GRC module with sections
  const getGrcModule = () => {
    const grcModule = modules.find(m => m.slug === 'grc' || m.slug === 'iso_grc');
    return grcModule || null;
  };

  // Get View and Edit permission type IDs
  const getViewEditPermTypes = () => {
    const viewType = permissionTypes.find(pt => pt.slug === 'view');
    const editType = permissionTypes.find(pt => pt.slug === 'edit' || pt.slug === 'write');
    return { viewType, editType };
  };

  // Toggle all permissions for a section (row)
  const toggleSectionAll = (sectionId: number, checked: boolean) => {
    const grcModule = getGrcModule();
    if (!grcModule) return;
    const { viewType, editType } = getViewEditPermTypes();
    
    const key = `${grcModule.id}-${sectionId}`;
    setPermMatrix(prev => {
      const newPerms: Record<number, boolean> = {};
      if (viewType) newPerms[viewType.id] = checked;
      if (editType) newPerms[editType.id] = checked;
      return { ...prev, [key]: newPerms };
    });
  };

  // Toggle all permissions for a type (column)
  const toggleTypeAll = (permTypeId: number, checked: boolean) => {
    const grcModule = getGrcModule();
    if (!grcModule) return;
    
    setPermMatrix(prev => {
      const newMatrix = { ...prev };
      grcModule.sections.forEach(section => {
        const key = `${grcModule.id}-${section.id}`;
        newMatrix[key] = { ...(newMatrix[key] || {}), [permTypeId]: checked };
      });
      return newMatrix;
    });
  };

  // Select all permissions
  const selectAllPermissions = (checked: boolean) => {
    const grcModule = getGrcModule();
    if (!grcModule) return;
    const { viewType, editType } = getViewEditPermTypes();
    
    setPermMatrix(prev => {
      const newMatrix = { ...prev };
      grcModule.sections.forEach(section => {
        const key = `${grcModule.id}-${section.id}`;
        const newPerms: Record<number, boolean> = {};
        if (viewType) newPerms[viewType.id] = checked;
        if (editType) newPerms[editType.id] = checked;
        newMatrix[key] = newPerms;
      });
      return newMatrix;
    });
  };

  // Check if all permissions for a column are selected
  const isColumnAllSelected = (permTypeId: number) => {
    const grcModule = getGrcModule();
    if (!grcModule) return false;
    return grcModule.sections.every(section => {
      const key = `${grcModule.id}-${section.id}`;
      return permMatrix[key]?.[permTypeId] === true;
    });
  };

  // Check if all permissions are selected
  const isAllSelected = () => {
    const { viewType, editType } = getViewEditPermTypes();
    if (!viewType || !editType) return false;
    return isColumnAllSelected(viewType.id) && isColumnAllSelected(editType.id);
  };

  // Save permissions for the role
  const handleSavePermissions = async () => {
    if (!editingRolePerms) return;
    setSavingPerms(true);
    try {
      const permissions: Array<{ module_id: number; section_id?: number; permission_type_id: number }> = [];
      
      // Convert matrix to array of permissions
      for (const [key, perms] of Object.entries(permMatrix)) {
        const [moduleId, sectionId] = key.split('-').map(Number);
        for (const [permTypeId, granted] of Object.entries(perms)) {
          if (granted) {
            permissions.push({
              module_id: moduleId,
              section_id: sectionId > 0 ? sectionId : undefined,
              permission_type_id: Number(permTypeId)
            });
          }
        }
      }
      
      const result = await authService.setPermissions(editingRolePerms.id, permissions);
      if (result.success) {
        setEditingRolePerms(null);
        setPermMatrix({});
      }
    } catch (e) {
      console.error('Failed to save permissions:', e);
    } finally {
      setSavingPerms(false);
    }
  };

  const handleAssignRole = async (userId: number, roleId: number) => {
    try {
      await authService.assignRole(userId, roleId);
      await loadUsers();
    } catch (e) {
      console.error('Assign role failed:', e);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
  };

  const getRoleBadgeColor = (role: string) => {
    const r = role?.toLowerCase() || '';
    if (r.includes('admin')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (r.includes('editor') || r.includes('edit')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    if (r.includes('audit')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const getActionBadgeColor = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('delete') || a.includes('failed') || a.includes('blocked')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (a.includes('create') || a.includes('success')) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    if (a.includes('update') || a.includes('change') || a.includes('reset')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    if (a.includes('login')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            {t.settings.title}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6">
          <nav className="flex gap-6">
            {isGlobalAdmin && (
            <button
              onClick={() => setActiveTab('general')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'general' 
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Key className="w-4 h-4 inline mr-2" />
              {t.nav.settings}
            </button>
            )}
            <button
              onClick={() => setActiveTab('users')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users' 
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              {t.rbac.users}
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'roles' 
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              {t.rbac.roles || 'Roles'}
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'audit' 
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <ClipboardList className="w-4 h-4 inline mr-2" />
              {t.rbac.auditLog}
            </button>
            {isGlobalAdmin && (
            <button
              onClick={() => setActiveTab('organizations')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'organizations' 
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Building2 className="w-4 h-4 inline mr-2" />
              Organizations
            </button>
            )}
          </nav>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              {/* Organization selector */}
              <div>
                <label htmlFor="orgSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization
                </label>
                <select
                  id="orgSelect"
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(Number(e.target.value))}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {organizations.length === 0 && <option value={0}>Loading organizations...</option>}
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Select which organization to configure AI settings for
                </p>
              </div>

              {/* API Key */}
              <div>
                <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  id="apiKey"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  API key for AI features. Stored securely in the database for this organization.
                </p>
              </div>

              {/* Model selector */}
              <div>
                <label htmlFor="apiModel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  AI Model
                </label>
                <select
                  id="apiModel"
                  value={apiModel}
                  onChange={(e) => setApiModel(e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="gpt-4o">GPT-4o (recommended)</option>
                  <option value="gpt-4o-mini">GPT-4o Mini (faster, cheaper)</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo (legacy)</option>
                </select>
              </div>

              {/* Error display */}
              {error && activeTab === 'general' && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {showSuccess && (
                <div className="p-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm rounded flex items-center justify-center">
                  API key saved to database successfully!
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingApiKey || selectedOrgId <= 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingApiKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingApiKey ? 'Saving...' : t.actions.save}
                </button>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              {/* Error display */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {/* User Form */}
              {showUserForm && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                    {editingUser ? t.rbac.editUser : t.rbac.addUser}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t.rbac.email} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        placeholder="user@example.com"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Used for login</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Password {editingUser ? '(leave empty to keep)' : ''} <span className="text-red-500">{!editingUser ? '*' : ''}</span>
                      </label>
                      <input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        placeholder={editingUser ? '••••••••' : 'Min. 6 characters'}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={userForm.first_name}
                        onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={userForm.last_name}
                        onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t.rbac.role}
                      </label>
                      <select
                        value={userForm.role_id}
                        onChange={(e) => setUserForm({ ...userForm, role_id: Number(e.target.value) })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      >
                        <option value={0}>— No role (assign later) —</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name} {role.role_type === 'system' ? '(system)' : role.role_type === 'org_admin' ? '(admin)' : '(custom)'}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {roles.length === 0 ? 'No roles defined. Create roles in the Roles tab first.' : 'Select a role to define what this user can access'}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => { setShowUserForm(false); setEditingUser(null); setError(''); }}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {t.actions.cancel}
                    </button>
                    <button
                      onClick={handleSaveUser}
                      disabled={savingUser}
                      className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingUser && <Loader2 className="w-3 h-3 animate-spin" />}
                      {t.actions.save}
                    </button>
                  </div>
                </div>
              )}

              {/* Add User Button + Refresh */}
              {!showUserForm && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAddUser}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4" />
                    {t.rbac.addUser}
                  </button>
                  <button
                    onClick={() => { loadUsers(); loadRoles(); }}
                    disabled={usersLoading}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              )}

              {/* Users Table */}
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-500">Loading users...</span>
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.email}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.role}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.status}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.lastLogin}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map(user => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mr-3">
                              <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.first_name} {user.last_name}
                              </span>
                              {user.is_global_admin ? (
                                <span className="ml-2 text-xs text-amber-600 dark:text-amber-400 font-semibold">Global Admin</span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {user.roles ? (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.roles)}`}>
                              {user.roles}
                            </span>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-400 italic">No role</span>
                              <select
                                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                defaultValue=""
                                onChange={(e) => {
                                  const rId = Number(e.target.value);
                                  if (rId > 0) handleAssignRole(user.id, rId);
                                }}
                              >
                                <option value="">Assign...</option>
                                {roles.map(r => (
                                  <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleUser(user)}
                            className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                              user.is_active 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200' 
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200'
                            }`}
                            title={user.is_active ? 'Click to deactivate' : 'Click to activate'}
                          >
                            {user.is_active ? t.status.active : t.status.inactive}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(user.last_login)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {!user.is_global_admin && (
                            <>
                              <button
                                onClick={() => handleEditUser(user)}
                                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                                title="Edit user"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm(`Are you sure you want to permanently delete user "${user.first_name} ${user.last_name}" (${user.email})? This action cannot be undone.`)) {
                                    try {
                                      const result = await authService.deleteUser(user.id);
                                      if (result.success) {
                                        await loadUsers();
                                      } else {
                                        setError(result.error || 'Failed to delete user');
                                      }
                                    } catch (e: any) {
                                      setError(e.message || 'Failed to delete user');
                                    }
                                  }
                                }}
                                className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                title="Delete user permanently"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No users found. Click &quot;Add User&quot; to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              {/* Permissions Matrix Panel */}
              {editingRolePerms && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Shield className="w-4 h-4 text-indigo-500" />
                      Permissions for: <span className="text-indigo-600 dark:text-indigo-400">{editingRolePerms.name}</span>
                    </h4>
                    <button
                      onClick={() => { setEditingRolePerms(null); setPermMatrix({}); }}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <strong>View:</strong> Read-only access to the section. <strong>Edit:</strong> Create, update, delete, and export data.
                  </p>
                  
                  {loadingPerms ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                      <span className="ml-2 text-gray-500">Loading permissions...</span>
                    </div>
                  ) : (() => {
                    const grcModule = getGrcModule();
                    const { viewType, editType } = getViewEditPermTypes();
                    
                    if (!grcModule || !viewType || !editType) {
                      return (
                        <div className="py-6 text-center text-gray-500 dark:text-gray-400">
                          No GRC module found. Run the RBAC setup script first.
                        </div>
                      );
                    }
                    
                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-100 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                Section
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                <div className="flex flex-col items-center gap-1">
                                  <span>View</span>
                                  <button
                                    onClick={() => toggleTypeAll(viewType.id, !isColumnAllSelected(viewType.id))}
                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                                  >
                                    {isColumnAllSelected(viewType.id) ? 'Deselect All' : 'Select All'}
                                  </button>
                                </div>
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                <div className="flex flex-col items-center gap-1">
                                  <span>Edit</span>
                                  <button
                                    onClick={() => toggleTypeAll(editType.id, !isColumnAllSelected(editType.id))}
                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                                  >
                                    {isColumnAllSelected(editType.id) ? 'Deselect All' : 'Select All'}
                                  </button>
                                </div>
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                <button
                                  onClick={() => selectAllPermissions(!isAllSelected())}
                                  className="px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50"
                                >
                                  {isAllSelected() ? 'Deselect All' : 'Select All'}
                                </button>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {grcModule.sections.map(section => {
                              const key = `${grcModule.id}-${section.id}`;
                              const viewChecked = permMatrix[key]?.[viewType.id] || false;
                              const editChecked = permMatrix[key]?.[editType.id] || false;
                              const rowAllChecked = viewChecked && editChecked;
                              
                              return (
                                <tr key={section.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    <span className="font-medium">{section.name}</span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <button
                                      onClick={() => togglePermission(grcModule.id, section.id, viewType.id)}
                                      className={`w-7 h-7 rounded border-2 flex items-center justify-center transition-colors mx-auto ${
                                        viewChecked 
                                          ? 'bg-indigo-600 border-indigo-600 text-white' 
                                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 hover:border-indigo-400'
                                      }`}
                                    >
                                      {viewChecked && <Check className="w-4 h-4" />}
                                    </button>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <button
                                      onClick={() => togglePermission(grcModule.id, section.id, editType.id)}
                                      className={`w-7 h-7 rounded border-2 flex items-center justify-center transition-colors mx-auto ${
                                        editChecked 
                                          ? 'bg-green-600 border-green-600 text-white' 
                                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 hover:border-green-400'
                                      }`}
                                    >
                                      {editChecked && <Check className="w-4 h-4" />}
                                    </button>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <button
                                      onClick={() => toggleSectionAll(section.id, !rowAllChecked)}
                                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    >
                                      {rowAllChecked ? '✓ All' : 'Select'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                  
                  <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => { setEditingRolePerms(null); setPermMatrix({}); }}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {t.actions.cancel}
                    </button>
                    <button
                      onClick={handleSavePermissions}
                      disabled={savingPerms}
                      className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingPerms ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Save Permissions
                    </button>
                  </div>
                </div>
              )}

              {/* Create Role Form */}
              {showRoleForm && !editingRolePerms && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Create New Role</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Role Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        placeholder="e.g., Risk Manager"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={newRoleDesc}
                        onChange={(e) => setNewRoleDesc(e.target.value)}
                        placeholder="e.g., Can edit risks only"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => { setShowRoleForm(false); setNewRoleName(''); setNewRoleDesc(''); }}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {t.actions.cancel}
                    </button>
                    <button
                      onClick={handleCreateRole}
                      disabled={savingRole || !newRoleName.trim()}
                      className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingRole && <Loader2 className="w-3 h-3 animate-spin" />}
                      Create Role
                    </button>
                  </div>
                </div>
              )}

              {!showRoleForm && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowRoleForm(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4" />
                    Create Role
                  </button>
                  <button
                    onClick={loadRoles}
                    disabled={rolesLoading}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <RefreshCw className={`w-4 h-4 ${rolesLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Create custom roles and assign them to users. Each role defines what sections a user can access.
              </p>

              {/* Roles List */}
              {rolesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-500">Loading roles...</span>
                </div>
              ) : (
                <div className="grid gap-3">
                  {roles.map(role => (
                    <div key={role.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-indigo-500" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{role.name}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            role.role_type === 'global_admin' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                            role.role_type === 'org_admin' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            {role.role_type}
                          </span>
                        </div>
                        {role.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{role.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">{role.user_count || 0} users</span>
                        <button
                          onClick={() => handleEditRolePermissions(role)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-700"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Edit Permissions
                        </button>
                        {/* Delete button - only for custom roles, not system roles */}
                        {role.role_type === 'custom' && (
                          <button
                            onClick={() => handleDeleteRole(role.id, role.name)}
                            disabled={deletingRoleId === role.id || (role.user_count && role.user_count > 0)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={role.user_count && role.user_count > 0 ? 'Cannot delete - role has assigned users' : 'Delete role'}
                          >
                            {deletingRoleId === role.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {roles.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Shield className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>No roles defined yet. Create a custom role to get started.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-indigo-600" />
                  {t.rbac.activity}
                  {auditTotal > 0 && <span className="text-xs text-gray-400">({auditTotal} entries)</span>}
                </h4>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      placeholder="Search actions, users..."
                      className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-56"
                    />
                  </div>
                  <button
                    onClick={loadAuditLog}
                    disabled={auditLoading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${auditLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {auditLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-500">Loading audit trail...</span>
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.timestamp}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.user}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.action}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.details}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {t.rbac.ipAddress}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {auditLog
                      .filter(entry => {
                        if (!auditSearch.trim()) return true;
                        const search = auditSearch.toLowerCase();
                        return (
                          entry.action?.toLowerCase().includes(search) ||
                          entry.email?.toLowerCase().includes(search) ||
                          entry.target_email?.toLowerCase().includes(search) ||
                          entry.description?.toLowerCase().includes(search) ||
                          entry.details?.toLowerCase().includes(search)
                        );
                      })
                      .map(entry => (
                      <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3" />
                            {formatDate(entry.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {entry.email || entry.target_email || 'System'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeColor(entry.action)}`}>
                            {entry.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={entry.description || entry.details || ''}>
                          {entry.description || entry.details || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                          {entry.ip_address || '-'}
                        </td>
                      </tr>
                    ))}
                    {auditLog.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No audit trail entries yet. Activities will appear here as users interact with the platform.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {/* Organizations Tab (Global Admin Only) */}
          {activeTab === 'organizations' && isGlobalAdmin && (
            <div className="space-y-4">
              {/* Error display */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
                  {error}
                </div>
              )}

              {/* Create Organization Form */}
              {showOrgForm && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-indigo-500" />
                    Create New Organization
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newOrgForm.name}
                        onChange={(e) => setNewOrgForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Acme Corporation"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={newOrgForm.contact_email}
                        onChange={(e) => setNewOrgForm(prev => ({ ...prev, contact_email: e.target.value }))}
                        placeholder="admin@acme.com"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={newOrgForm.description}
                        onChange={(e) => setNewOrgForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        License Type
                      </label>
                      <select
                        value={newOrgForm.license_type}
                        onChange={(e) => setNewOrgForm(prev => ({ ...prev, license_type: e.target.value }))}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      >
                        <option value="trial">Trial</option>
                        <option value="standard">Standard</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Users
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={newOrgForm.max_users}
                        onChange={(e) => setNewOrgForm(prev => ({ ...prev, max_users: parseInt(e.target.value) || 10 }))}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => { setShowOrgForm(false); setNewOrgForm({ name: '', description: '', contact_email: '', license_type: 'standard', max_users: 10 }); }}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateOrganization}
                      disabled={savingOrg || !newOrgForm.name.trim()}
                      className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingOrg && <Loader2 className="w-3 h-3 animate-spin" />}
                      Create Organization
                    </button>
                  </div>
                </div>
              )}

              {/* Create Org Admin Form */}
              {showOrgAdminForm !== null && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 mb-4 border border-emerald-200 dark:border-emerald-800">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-emerald-600" />
                    Create Organization Admin for: <span className="text-emerald-600 dark:text-emerald-400">{allOrganizations.find(o => o.id === showOrgAdminForm)?.name}</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={newOrgAdminForm.email}
                        onChange={(e) => setNewOrgAdminForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="admin@example.com"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={newOrgAdminForm.password}
                        onChange={(e) => setNewOrgAdminForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Min 6 characters"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newOrgAdminForm.first_name}
                        onChange={(e) => setNewOrgAdminForm(prev => ({ ...prev, first_name: e.target.value }))}
                        placeholder="John"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={newOrgAdminForm.last_name}
                        onChange={(e) => setNewOrgAdminForm(prev => ({ ...prev, last_name: e.target.value }))}
                        placeholder="Doe"
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => { setShowOrgAdminForm(null); setNewOrgAdminForm({ email: '', first_name: '', last_name: '', password: '' }); }}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleCreateOrgAdmin(showOrgAdminForm)}
                      disabled={savingOrgAdmin || !newOrgAdminForm.email || !newOrgAdminForm.first_name || !newOrgAdminForm.password}
                      className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingOrgAdmin && <Loader2 className="w-3 h-3 animate-spin" />}
                      Create Admin
                    </button>
                  </div>
                </div>
              )}

              {/* Header actions */}
              {!showOrgForm && !showOrgAdminForm && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowOrgForm(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4" />
                    Create Organization
                  </button>
                  <button
                    onClick={loadAllOrganizations}
                    disabled={orgsLoading}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <RefreshCw className={`w-4 h-4 ${orgsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Manage organizations and their administrators. Each organization has isolated users, roles, and data.
              </p>

              {/* Organizations List */}
              {orgsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-500">Loading organizations...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {allOrganizations.map(org => (
                    <div key={org.id} className="bg-white dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-indigo-500" />
                            <span className="text-base font-medium text-gray-900 dark:text-white">{org.name}</span>
                            {org.license_type && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                org.license_type === 'enterprise' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                                org.license_type === 'professional' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                org.license_type === 'trial' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                              }`}>
                                {org.license_type}
                              </span>
                            )}
                            {org.openai_api_key && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                API Key Set
                              </span>
                            )}
                          </div>
                          {org.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{org.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 dark:text-gray-500">
                            {org.contact_email && <span>Contact: {org.contact_email}</span>}
                            {org.max_users && <span>Max Users: {org.max_users}</span>}
                            {org.created_at && <span>Created: {formatDate(org.created_at)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => setShowOrgAdminForm(org.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-700"
                            title="Create an admin user for this organization"
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            Add Admin
                          </button>
                          <button
                            onClick={() => handleDeleteOrganization(org.id)}
                            disabled={deletingOrgId === org.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-700 disabled:opacity-50"
                            title="Delete organization and all its data"
                          >
                            {deletingOrgId === org.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {allOrganizations.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>No organizations found. Create one to get started.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            {t.actions.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};
